import React from "react";
import { useArrMrr } from "@/services/context/ArrMrrContext";
import Text from "@/components/ui/text";
import { LOST_DEAL, WON_DEAL, colorsByStatus } from "../KanbanBoard";
import { formatNumber } from "@/utils/numbers";
import { isOverdue, simpleDate, toLocalDate } from "@/utils/datetime";
import { useTranslation } from "react-i18next";
import { ArrowUpRight, CloseDate, User } from "@dreamhub/designsystem/icons";
import { cn, getHexColor } from "@/utils/utils";
import {
  DealColumn,
  DealData,
  DealMilestone,
  DealStatus
} from "@/pages/deals/types";
import { useUsers } from "@/entities/users/hooks";
import { getColorForMilestoneByType } from "@/components/shared/Milestones";
import { daysToWeeks, weeksToDays } from "date-fns";
import {
  useDealsNavigation,
  getDealDetailedViewUrl
} from "@/services/hooks/entities/deal/useDealsNavigation";
import IconContainer from "@/components/ui/icon-container";
import AlertsIndication from "./AlertsIndication";
import { IconButton } from "@/components/ui/icon-button";
import { Divider } from "@/components/ui/divider";
import DealNextSteps from "../../DealNextSteps/DealNextSteps";
import DetailChip from "./DetailChip";
import { StatusBadge } from "@/components/ui/status-badge";
import { dealStatusColors } from "@/pages/deals/components/DealsTable/DealsTable";
import { getEnumTKey } from "@/utils/strings";
import { isEmpty, upperFirst } from "lodash";
import ClickableLink from "@/components/ui/clickable-link";

interface Props {
  item: DealData;
  avgDurationWeeks: number;
  milestone: DealMilestone;
  draggedOverArea: string | null;
  isDragged: boolean;
  sourceColumnIndex: number | null;
  onCardClick: (item: DealData | null) => void;
  isDealDrawerOpen: boolean;
  dragHandleProps?: any;
}

const DealCard: React.FC<Props> = ({
  item,
  avgDurationWeeks,
  milestone,
  draggedOverArea,
  isDragged,
  sourceColumnIndex,
  onCardClick,
  isDealDrawerOpen,
  dragHandleProps
}) => {
  const { t } = useTranslation();
  const { getValue: getArrOrMrrValue, fieldName: arrOrMrrField } = useArrMrr();

  const {
    id,
    name,
    expectedCloseAt,
    companyName,
    managedBy,
    alerts,
    hasNextSteps,
    status
  } = item;
  const arrMrrValue = getArrOrMrrValue(item);

  const isExpectedCloseAtOverdue = isOverdue(expectedCloseAt);

  const { navigateToDetailedView } = useDealsNavigation();
  const ownerData = useUsers([managedBy]).users[0];

  const daysInStage = item.daysInStage || 0;
  const weeksInStage = daysToWeeks(daysInStage);

  const daysAboveAvg = Math.max(0, daysInStage - weeksToDays(avgDurationWeeks));

  const { value, currencySign } = formatNumber(
    arrMrrValue || 0,
    0,
    true,
    false
  );

  const wonDisabled = draggedOverArea === WON_DEAL && sourceColumnIndex === 0;

  const openDetailedView = () => {
    navigateToDetailedView(id);
  };

  const statusValueKey = getEnumTKey(
    "stakeholders.dealsModal.table",
    DealColumn.STATUS,
    DealStatus,
    status as DealStatus
  );

  const getBorderColor = () => {
    if (isDragged) {
      if (wonDisabled) {
        return colorsByStatus.disabled;
      }
      if (draggedOverArea === WON_DEAL || draggedOverArea === LOST_DEAL) {
        return colorsByStatus[draggedOverArea];
      }
    }
    return getColorForMilestoneByType(milestone, "border");
  };

  const getBackground = () => {
    const getGradient = (colorVar: string) => {
      const hexColor = getHexColor(colorVar);
      return `linear-gradient(to top, ${hexColor}66, ${hexColor}22), #1c2432`;
    };
    if (isDragged) {
      if (wonDisabled) {
        return getGradient(colorsByStatus.disabled);
      }
      if (draggedOverArea === WON_DEAL || draggedOverArea === LOST_DEAL) {
        return getGradient(colorsByStatus[draggedOverArea]);
      }
      return colorsByStatus.dragged;
    }
    return colorsByStatus.default;
  };

  return (
    <div
      data-test={`deals-kanban-card-${id}`}
      className="flex mb-3 p-2 gap-[10px] border-[0.5px] min-h-20 rounded-xl group transition-all duration-300 ease-in-out"
      style={
        {
          background: getBackground(),
          borderColor: isDealDrawerOpen
            ? getColorForMilestoneByType(milestone, "border")
            : "var(--grey-750)",
          "--border-color": getBorderColor(),
          "--border-default": getColorForMilestoneByType(milestone, "dim"),
          "--border-hover": getColorForMilestoneByType(milestone, "border")
        } as React.CSSProperties
      }
    >
      <div
        className={cn(
          "w-[5px] rounded self-stretch transition-colors duration-200",
          isDragged
            ? "bg-[var(--border-color)]"
            : "bg-[var(--border-default)] group-hover:bg-[var(--border-hover)]"
        )}
      />
      <div className="flex flex-col gap-[6px] w-full">
        <div
          {...dragHandleProps}
          className="cursor-pointer"
          onClick={() => onCardClick(item)}
          role={dragHandleProps?.role ?? "button"}
          tabIndex={dragHandleProps?.tabIndex ?? 0}
        >
          <div className="flex w-full justify-between">
            <div className="min-w-0 flex-1">
              <Text variant="label" className="truncate pl-1 w-60">
                {upperFirst(name)}
              </Text>
            </div>
            <ClickableLink
              href={getDealDetailedViewUrl(id)}
              onClick={openDetailedView}
              stopPropagation={true}
            >
              <IconButton
                size="md"
                className="group-hover:opacity-100 opacity-0"
                data-test="deal-opendetailed-tooltip"
                tooltipText={t("deals.openDetailedTooltip")}
              >
                <ArrowUpRight />
              </IconButton>
            </ClickableLink>
          </div>
          <div className="flex flex-col gap-1">
            <div className="flex items-center gap-1">
              {isEmpty(managedBy) ? (
                <div className="flex gap-1 items-center">
                  <div className="border-[1.2px] border-dashed border-border-border3 rounded-md p-[2px]">
                    <IconContainer size="xs" color="tertiary">
                      <User />
                    </IconContainer>
                  </div>
                  <Text variant="bodySecondary" className="text-tertiary">
                    {t("deals.emptyManagedBy")}
                  </Text>
                </div>
              ) : (
                <DetailChip
                  fieldName={DealColumn.MANAGED_BY}
                  ownerData={ownerData}
                />
              )}
              <DetailChip
                fieldName={arrOrMrrField}
                triggerValue={`${currencySign}${value}`}
                tooltipValue={`${currencySign}${value || 0}`}
              />
              {(companyName?.length ?? 0) <= 20 && (
                <DetailChip
                  fieldName={DealColumn.COMPANY_NAME}
                  triggerValue={companyName ?? ""}
                  tooltipValue={companyName ?? ""}
                />
              )}
            </div>
            {(companyName?.length ?? 0) > 20 && (
              <div className="flex items-center gap-1">
                <DetailChip
                  fieldName={DealColumn.COMPANY_NAME}
                  triggerValue={companyName ?? ""}
                  tooltipValue={companyName ?? ""}
                />
              </div>
            )}
            <div className="flex items-center gap-1">
              <DetailChip
                fieldName="timeInStage"
                triggerValue={`${weeksInStage}${t("stageAverageDuration.weeksUnit")}`}
                tooltipValue={`${weeksInStage} ${t("stageAverageDuration.weeks")}`}
                daysAboveAvg={daysAboveAvg}
              />
              {expectedCloseAt ? (
                <DetailChip
                  fieldName={DealColumn.EXPECTED_CLOSE_AT}
                  triggerValue={simpleDate(
                    toLocalDate(expectedCloseAt),
                    "short",
                    true
                  )}
                  tooltipValue={
                    isExpectedCloseAtOverdue
                      ? t("deals.expectedCloseAtOverdue")
                      : simpleDate(toLocalDate(expectedCloseAt), "short", false)
                  }
                  isExpectedCloseAtOverdue={isExpectedCloseAtOverdue}
                />
              ) : (
                <div className="p-1 flex gap-1 items-center">
                  <IconContainer color="tertiary">
                    <CloseDate />
                  </IconContainer>
                  <Text className="text-warning">
                    {t("deals.emptyExpectedCloseAt")}
                  </Text>
                </div>
              )}
            </div>
            {status === DealStatus.STUCK && (
              <StatusBadge
                statusColor={dealStatusColors[status]}
                className="text-secondary py-[2px] px-[6px] gap-1 text-base"
              >
                {t(statusValueKey)}
              </StatusBadge>
            )}
          </div>
        </div>
        <Divider />
        <div
          className="relative flex w-full justify-between cursor-auto"
          onClick={(e) => e.stopPropagation()}
        >
          <DealNextSteps dealId={id} hasNextSteps={hasNextSteps || false} />
          {alerts && (
            <div className="absolute top-0 right-0">
              <AlertsIndication dealId={id} alerts={alerts} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DealCard;
